<template>
    <wrapper-features-templates-operations
    fullSizeContent
    :customBreadcrumbs="{ currentArea: t('approvals.operators.edit.customBreadcrumbs') }"
    :showLoader="showLoader"
    :showOverlayLoader="showOverlayLoader"
    :applyErrorState="applyErrorState"
    :stepperParams="stepperParams"
    :activeStep="activeStep"
    :showStepper="!showLoader && !applyErrorState && !isEdited"
    :showFooter="!showLoader && !applyErrorState && !isEdited"
    :footerButtonsParams="footerButtonsParams.filter(ele => !ele.hidden)"
    :scrollValue="scrollValue"
    :tokenTitleAppend="t('approvals.operators.edit.tokenTitleAppend')"
    :feedbackOperationData="headerData"
    @clearScrollToValue="scrollValue = null"
    @challengeSuccessHandler="challengeSuccessHandler"
    @challengeErrorHandler="errorHandler($event, editOperatorHandler)"
    @backOrCloseClicked="emit('backOrCloseClicked')">
        <component
        v-if="!showLoader && !applyErrorState"
        :is="getActiveStepComponent"
        :operatorData="operatorData"
        :accounts="accounts"
        :checkErrors="checkErrors"
        :headerData="headerData"
        @scrollTo="scrollValue = $event"
        @showProfilesModal="showProfilesModal = true"
        @updateData="updateDataHandler($event)"
        @fieldHasError="fieldHasErrorHandler($event)"
        @addAccounts="addAccountsHandler"
        @accountSelected="selectAccountHandler($event)"
        @addOperationLimits="accounts[$event].operationLimits.push({...operationLimitEmptyObj})"
        @operationType="operationTypeHandler($event)"
        @operationLimits="operationLimitsHandler($event)"
        @deleteOperationRow="deleteOperationRowHandler($event)"
        @duplicateAccountBlock="duplicateAccountBlockHandler"
        @resetAccountValues="resetAccountValues"
        @resetRowValues="resetRowValues"
        @removeAccountBlock="removeAccountBlock($event)"
        @accountBlockFinishUpdate="accountBlockFinishUpdateHandler"
        @createNewOperator="clearFields()"/>
    </wrapper-features-templates-operations>
    <!-- Feedback Toast -->
    <toast-generic
    v-if="showFeedbackToast"
    :toastType="feedbackToastData.toastType"
    :feedbackText="feedbackToastData.text"
    :buttonsParams="feedbackToastData.buttons"
    @hideMe="showFeedbackToast = false"/>
    <!-- Profiles Modal -->
    <operators-profiles-modal v-if="showProfilesModal" @hideMe="showProfilesModal = false"/>
    <!-- Duplicate Account Data Modal -->
    <duplicate-account-info-modal
    v-if="showDuplicateAccountInfoModal"
    :accounts="accounts"
    :accountToCopyIndex="accountToCopyIndex"
    @duplicateInfoToAccounts="duplicateInfoToAccounts"
    @hideMe="showDuplicateAccountInfoModal = false"/>
    <!-- Cant Copy Data Modal -->
    <modal-centered-generic
    v-if="showCantDuplicateAccountsModal"
    titleIcon="info"
    leftButtonIcon="check-line"
    :title="t('approvals.operators.edit.cantDuplicateAccountsModal.title')"
    :text="t('approvals.operators.edit.cantDuplicateAccountsModal.text')"
    :leftButtonText="t('approvals.operators.edit.cantDuplicateAccountsModal.button')"
    @hideMe="showCantDuplicateAccountsModal = false"/>
</template>

<script setup>
import WrapperFeaturesTemplatesOperations from '@/components/wrappers/featuresTemplates/Operations'
import ToastGeneric from '@/components/generic/toasts/Generic'
import OperatorsProfilesModal from '@/components/features/approvals/operators/edit/ProfilesModal'
import DuplicateAccountInfoModal from '@/components/features/approvals/operators/edit/DuplicateAccountInfoModal'
import ModalCenteredGeneric from '@/components/generic/modals/centered/Generic'

/* Vue & Assets */
import { ref, computed, defineAsyncComponent, watch } from 'vue'
import { sucessObjectConstrutor, errorObjectConstructor, emptyObjectConstructor } from '@/assets/scripts/toastsHelpers'
import { getElementOffsetTopPosition } from '@/assets/scripts/htmlManipulators'

/* Store */
import { mapActions, mapGetters } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* Emmitters */
const emit = defineEmits(['backOrCloseClicked'])

const props = defineProps({
    operatorId: Number
})

/* mapActions & mapGetters */
const { requestOperatorDetail, requestProfiles, requestOperationsTypes, requestAccounts, requestPlafondsAndLimits, editOperator } = mapActions(moduleMapper.APPROVALS_OPERATORS)
const { getAccounts, getOperationsTypesList, getPlafondsAndLimits, getProfilesForDropdown } = mapGetters(moduleMapper.APPROVALS_OPERATORS)

/* Generic Data */
const showLoader = ref(false)
const showOverlayLoader = ref(false)
const applyErrorState = ref(false)
const scrollValue = ref()
const operatorInfo = ref()
const showProfilesModal = ref(false)

/*************************************************
*               Steps/Footer Data                *
*************************************************/
const activeStep = ref(1)
const stepperParams = ref([
    { icon: 'edit-pencil', label: t('approvals.operators.edit.steps.data') },
    { icon: 'list', label: t('approvals.operators.edit.steps.validation') }
])

const getActiveStepComponent = computed(() => {
    const stepNumber = activeStep.value
    return defineAsyncComponent(() => import('@/components/features/approvals/operators/edit/Step' + stepNumber))
})

const goToStep = stepNumber => {
    activeStep.value = stepNumber
    scrollValue.value = 0
}

const footerButtonsParams = computed(() => {
    return [{
        label: t('approvals.operators.edit.footer.clear'),
        buttonPosition: 'left',
        hidden: activeStep.value !== 1,
        callbackFunction: () => { setOperatorInitialInfo() }
    }, {
        label: t('approvals.operators.edit.footer.confirm'),
        icon: 'arrow-right-full',
        buttonType: 'primary',
        iconPosition: 'right',
        hidden: activeStep.value !== 1,
        callbackFunction: () => { validateHandler() }
    }, {
        label: t('approvals.operators.edit.footer.edit'),
        icon: 'arrow-left-full',
        hidden: activeStep.value !== 2,
        callbackFunction: () => { activeStep.value-- }
    }, {
        label: t('approvals.operators.edit.footer.validate'),
        icon: 'check-line',
        buttonType: 'primary',
        iconPosition: 'right',
        hidden: activeStep.value !== 2,
        callbackFunction: () => { editOperatorHandler() }
    }]
})

/*************************************************
*                                                *
*                      Data                      *
*                                                *
*************************************************/
const operatorData = ref({
    username: { value: '' },
    name: { value: '', errorState: false },
    email: { value: '', errorState: false },
    nif: { value: '', errorState: false },
    msisdn: { value: '', errorState: false },
    weight: { value: '', errorState: false },
    profileId: { value: '', errorState: false },
    accessToNewAccounts: { value: '', errorState: false },
    defaultAccountId: { value: '', errorState: false },
    accountIds: { value: [], errorState: false }
})

const mandatoryList = [
    'name',
    'email',
    'msisdn',
    'accessToNewAccounts',
    'profileId',
    'weight',
    'accountIds'
]

const accounts = ref([{ operationLimits: [] }])

const setOperatorInitialInfo = () => {
    operatorData.value = {
        username: { value: operatorInfo.value.username },
        name: { value: operatorInfo.value.name, errorState: false },
        email: { value: operatorInfo.value.email, errorState: false },
        nif: { value: operatorInfo.value.nif || '', errorState: false },
        msisdn: { value: operatorInfo.value.msisdn || '', errorState: false },
        weight: { value: operatorInfo.value.weight || '', errorState: false },
        profileId: { value: operatorInfo.value.profileId, errorState: false },
        accessToNewAccounts: { value: operatorInfo.value.accessToNewAccounts, errorState: false },
        defaultAccountId: { value: operatorInfo.value.defaultAccountId, errorState: false },
        accountIds: { value: operatorInfo.value.accountIds, errorState: false }
    }

    accounts.value = operatorInfo.value.accountIds.map(accountId => {
        return {
            ...getAccounts.value.bankAccountView.find(obj => obj.id === accountId),
            operationLimits: [...setInitialAccountsOperationsLimits(accountId)]
        }
    })

    checkErrors.value = false
    isEdited.value = false
    activeStep.value = 1
}

const setInitialAccountsOperationsLimits = accountId => {
    if (!operatorInfo.value.accountAccessConfig[accountId]) return []

    const initialAccountOperationObj = operatorInfo.value.accountAccessConfig[accountId].operationLimits
    const limitsArray = []

    for (const key in initialAccountOperationObj) {
        limitsArray.push({
            operationType: { value: parseInt(key), errorState: false },
            plafond: { value: initialAccountOperationObj[key].dailyPlafond, errorState: false },
            transactionLimit: { value: initialAccountOperationObj[key].transactionLimit, errorState: false }
        })
    }

    return limitsArray
}

/*************************************************
*                                                *
*                    Handlers                    *
*                                                *
*************************************************/
const updateDataHandler = ({ param, value }) => {
    operatorData.value[param] = {
        value: value,
        errorState: false,
        fieldHasError: false
    }

    if (param === 'profileId') handleProfileChange(value)
}

const fieldHasErrorHandler = (param) => {
    operatorData.value[param] = {
        ...operatorData.value[param],
        fieldHasError: true
    }
}

const handleProfileChange = (value) => {
    if (value !== 4 && !accounts.value.length) {
        showOverlayLoader.value = true
        setTimeout(() => {
            addAccountsHandler()
        }, 100)
    } else if (value === 1 && !!accounts.value.length) {
        clearAccountsPlafondsAndLimits()
    } else if (value === 2 && !!accounts.value.length) {
        setAccountsDefaultValues()
    } else if (value !== 4) {
        clearAccountsOperations()
    } else if (value === 4) {
        accounts.value = accounts.value.map(account => {
            return {
                ...account,
                operationLimits: []
            }
        })
    }
}

const addAccountsHandler = () => {
    if (!accounts.value.length && operatorData.value.profileId.value !== 4) {
        const defaultAccount = getAccounts.value.bankAccountView.find(account => account.default)
        const plafondsAndLimits = getPlafondsAndLimits.value.find(obj => obj.account.id === defaultAccount.id).operationTypesWithPlafondsAndTransactionLimits

        const temp = {
            ...defaultAccount,
            operationLimits: getOperationsTypesList.value.reduce((acc, type) => {
                if (plafondsAndLimits.find(obj => obj.operationType.id === type.id)) {
                    const plafondAndLimit = plafondsAndLimits.find(obj => obj.operationType.id === type.id)

                    acc.push({
                        operationType: { value: plafondAndLimit.operationType.id, errorState: false },
                        plafond: { value: operatorData.value.profileId.value === 2 ? plafondAndLimit.plafondAndTransactionLimit.plafond : '', errorState: false },
                        transactionLimit: { value: operatorData.value.profileId.value === 2 ? plafondAndLimit.plafondAndTransactionLimit.transactionLimit : '', errorState: false }
                    })
                }
                return acc
            }, [])
        }

        operatorData.value.accountIds.value.push(defaultAccount.id)
        operatorData.value.defaultAccountId.value = defaultAccount.id
        accounts.value.push(temp)
    } else accounts.value.push({ operationLimits: [] })
}

const clearAccountsPlafondsAndLimits = () => {
    accounts.value = accounts.value.map(account => {
        return {
            ...account,
            operationLimits: account.operationLimits.map(operation => {
                return {
                    ...operation,
                    plafond: { value: '', errorState: false },
                    transactionLimit: { value: '', errorState: false }
                }
            })
        }
    })
}

const setAccountsDefaultValues = () => {
    accounts.value = accounts.value.map((account, accountIndex) => {
        return {
            ...account,
            operationLimits: account.operationLimits.map(operationRow => {
                return applyDefaultValuesForOperation(accountIndex, operationRow)
            })
        }
    })
}

const clearAccountsOperations = () => {
    accounts.value = accounts.value.map(account => {
        return { ...account, operationLimits: [] }
    })
}

const resetAccountValues = accountIndex => {
    accounts.value[accountIndex] = {
        ...accounts.value[accountIndex],
        operationLimits: accounts.value[accountIndex].operationLimits.map(operationRow => {
            return applyDefaultValuesForOperation(accountIndex, operationRow)
        })
    }
}

const resetRowValues = ({ value, index }) => {
    accounts.value[index] = {
        ...accounts.value[index],
        operationLimits: accounts.value[index].operationLimits.map((operationRow, i) => {
            if (i !== value) return operationRow
            return applyDefaultValuesForOperation(index, operationRow)
        })
    }
}

const applyDefaultValuesForOperation = (accountIndex, operationRow) => {
    const plafondsAndLimits = getPlafondsAndLimits.value.find(obj => obj.account.id === accounts.value[accountIndex].id).operationTypesWithPlafondsAndTransactionLimits
    const plafondAndLimit = plafondsAndLimits.find(obj => obj.operationType.id === operationRow.operationType.value)

    return {
        ...operationRow,
        plafond: { value: plafondAndLimit.plafondAndTransactionLimit.plafond, errorState: false },
        transactionLimit: { value: plafondAndLimit.plafondAndTransactionLimit.transactionLimit, errorState: false }
    }
}

const selectAccountHandler = ({ value, index }) => {
    /* If the account selected changing was the default one, we will also change the defaultAccountId */
    if (operatorData.value.accountIds.value[index] === operatorData.value.defaultAccountId.value) {
        operatorData.value.defaultAccountId.value = value
    }

    operatorData.value.accountIds.value.length < index ? operatorData.value.accountIds.value.push(value) : operatorData.value.accountIds.value[index] = value

    accounts.value[index] = {
        ...getAccounts.value.bankAccountView.find(ele => ele.id === value),
        operationLimits: Object.keys(accounts.value[index]).includes('operationLimits') ? accounts.value[index].operationLimits : []
    }

    accounts.value[0] = {
        ...accounts.value[0],
        errorState: false
    }
}

const operationTypeHandler = ({ value, index }) => {
    accounts.value[index].operationLimits[value.rowId].operationType = {
        value: value.selectedOption,
        errorState: false
    }
}

const operationLimitEmptyObj = {
    operationType: { value: '', errorState: false },
    plafond: { value: '', errorState: false },
    transactionLimit: { value: '', errorState: false }
}

const operationLimitsHandler = ({ value, index }) => {
    accounts.value[index].operationLimits[value.rowId][value.inputIdentifier] = {
        value: value.inputValue ?? '',
        errorState: false
    }
}

const deleteOperationRowHandler = ({ value, index }) => {
    accounts.value[index].operationLimits = accounts.value[index].operationLimits.filter((ele, index) => index !== value)
}

const accountToCopyIndex = ref(null)
const showDuplicateAccountInfoModal = ref(false)
const showCantDuplicateAccountsModal = ref(false)

const duplicateAccountBlockHandler = $event => {
    if (accounts.value.length === getAccounts.value.length) {
        showCantDuplicateAccountsModal.value = true
        return
    }

    accountToCopyIndex.value = $event
    showDuplicateAccountInfoModal.value = true
}

const duplicateInfoToAccounts = accountsToCopyInfo => {
    showOverlayLoader.value = true
    accountsToUpdate.value = accountsToCopyInfo.length
    triggerDuplicateSucessToast.value = true

    setTimeout(() => {
        accountsToCopyInfo.forEach(accountId => {
            const accountInfo = getAccounts.value.bankAccountView.find(obj => obj.id === accountId)
            const tempCopy = []

            accounts.value[accountToCopyIndex.value].operationLimits.forEach(ele => {
                tempCopy.push({ ...ele })
            })

            accounts.value.push({ ...accountInfo, operationLimits: tempCopy })
            operatorData.value.accountIds.value.push(accountId)
        })
    }, 100)
}

const accountsToUpdate = ref(0)
const triggerDuplicateSucessToast = ref(false)
const accountBlockFinishUpdateHandler = () => {
    if (accountsToUpdate.value) accountsToUpdate.value--
    else {
        showOverlayLoader.value = false
        if (triggerDuplicateSucessToast.value) {
            feedbackToastData.value = sucessObjectConstrutor(t('approvals.operators.edit.successDuplicateMessage'))
            showFeedbackToast.value = true
            triggerDuplicateSucessToast.value = false
        }
    }
}

const removeAccountBlock = (value) => {
    accounts.value = accounts.value.filter((el, index) => index !== value)
    operatorData.value.accountIds.value = operatorData.value.accountIds.value.filter((el, index) => index !== value)
}

/*************************************************
*                                                *
*               Initial Requests Logic           *
*                                                *
*************************************************/
const initialRequests = () => {
	showLoader.value = true

    /* We are using a Promise all to save sometime, calling all api's at the same time, since they dont depend on eachother */
    Promise.all([
        requestOperatorDetail(props.operatorId),
        requestAccounts(),
        requestOperationsTypes(),
        requestProfiles(),
        requestPlafondsAndLimits()
    ]).then(response => {
        operatorInfo.value = response[0] /* first item of response is the response of the first request */
        setOperatorInitialInfo()
        showLoader.value = false
    }).catch((error) => {
        if (error.toString() === 'Cancel') return
        applyErrorState.value = true
        errorHandler(error.response.data, initialRequests)
    })
}

/*************************************************
*                Form Logic Data                 *
*************************************************/
const checkErrors = ref(false)

const fieldsHaveErrors = object => {
    for (const key in object) {
        if (object[key].fieldHasError) return true
    }
    return false
}

const validateHandler = () => {
    checkErrors.value = false
    for (const param in operatorData.value) {
        switch (param) {
            case 'nif' :
            case 'username':
                break
            case 'weight':
                if (operatorData.value.profileId.value === 1 && !operatorData.value.weight.value) {
                    operatorData.value.weight.errorState = true
                    checkErrors.value = true
                }
                break
            case 'profileId':
                if (!operatorData.value.profileId.value.toString().length) {
                    operatorData.value.profileId.errorState = true
                    checkErrors.value = true
                }
                break
            case 'accessToNewAccounts':
                if (typeof operatorData.value.accessToNewAccounts.value !== 'boolean') {
                    operatorData.value.accessToNewAccounts.errorState = true
                    checkErrors.value = true
                }
                break
            case 'defaultAccountId':
                if (operatorData.value.accountIds.value.length && !operatorData.value.defaultAccountId.value.toString().length) {
                    operatorData.value.defaultAccountId.errorState = true
                    checkErrors.value = true
                }
                break
            case 'canProceed':
                if (!operatorData.value.canProceed.value) {
                    checkErrors.value = true
                    operatorData.value.canProceed.errorState = true
                }
                break
            case 'accountIds':
                if (operatorData.value.profileId.value !== 4 && !operatorData.value.accountIds.value.length) {
                    operatorData.value[param].errorState = true
                    checkErrors.value = true
                }
                break
            default:
                if (!operatorData.value[param].value.length) {
                    operatorData.value[param].errorState = true
                    checkErrors.value = true
                }
                break
        }
    }

    if (accounts.value.length) {
        accounts.value[0] = {
            ...accounts.value[0],
            errorState: true
        }

        accounts.value.forEach((account, accountIndex) => {
            if (account.id) {
                accounts.value[0] = {
                    ...accounts.value[0],
                    errorState: false
                }
            }

            if (account.operationLimits.length > 0) {
                account.operationLimits.forEach((operation, operationIndex) => {
                    for (const key in operation) {
                        if (!operation[key].value.toString().length) {
                            checkErrors.value = true
                            accounts.value[accountIndex].operationLimits[operationIndex][key].errorState = true
                        }
                    }
                })
            }
        })
    }

    /*
    * Logic to scroll to first emptyField,
    * IMPORTANT THIS IS NOT THE COMMON WAY TO DO THIS, SO IF U CAME HERE TO COPY THIS LOGIC, GO AWAY
    */
    const elementToScrollTo = mandatoryList.find(element => {
        if (element !== 'canProceed' && operatorData.value[element].errorState) return element

        if (element === 'weight' && operatorData.value.profileId.value === 1 && !operatorData.value.weight.value.length) {
            return element
        }
    })

    scrollValue.value = getElementOffsetTopPosition(elementToScrollTo)

    if (!checkErrors.value && !fieldsHaveErrors(operatorData.value)) {
        accounts.value = accounts.value.filter(account => {
            if (account.id) return account
        })
         activeStep.value++
    }
}

/*************************************************
*                                                *
*                   Edit Request                 *
*                                                *
*************************************************/
const accountAccessConfigChanged = () => {
    /* validating if accounts changed */
	if (operatorInfo.value.accountIds.length !== operatorData.value.accountIds.length) return true

    for (let index = 0; index < accounts.value.length; index++) {
        const account = accounts.value[index]
        /* If we find id's that are not in our initial array we will return true and don't need to check operations and limits */
        if (!operatorInfo.value.accountIds.find(item => item === account.account.id)) return true

        /* if account existed, we will check if the operations are the same */
        const previousInfo = operatorInfo.value.accountAccessConfig[account.account.id]

        /* If the length isnt the same, we know something change for sure */
        if (account.operationLimits.length !== Object.keys(previousInfo.operationLimits).length) return true

        for (let index = 0; index < account.operationLimits.length; index++) {
            const operationType = account.operationLimits[index]
            /* If we find id's that are not in our initial array we will return true and don't need to check operations and limits */
            if (!previousInfo.operationLimits[operationType.operationType]) return true

            /* If operation existed already, we need to check if limits changed */
            if (operationType.plafond.value !== previousInfo.operationLimits[operationType.operationType].dailyPlafond ||
                operationType.transactionLimit.value !== previousInfo.operationLimits[operationType.operationType].transactionLimit) return true
        }
    }
    return false
}

const editOperatorHandler = async () => {
    showOverlayLoader.value = true

    const payload = {
        name: operatorData.value.name.value,
        email: operatorData.value.email.value,
        nif: operatorData.value.nif.value,
        msisdn: operatorData.value.msisdn.value,
        weight: operatorData.value.profileId.value === 1 ? operatorData.value.weight.value : null,
        profileId: getProfilesForDropdown.value.find(obj => obj.id === operatorData.value.profileId.value).mdwId,
        accessToNewAccounts: operatorData.value.accessToNewAccounts.value,
        defaultAccountId: operatorData.value.defaultAccountId.value,
        accountIds: operatorData.value.accountIds.value
    }

    /* If no change was made in accounts and operationsLimits, don't send the info of accountConfig */
    if (accountAccessConfigChanged()) {
        payload.accountAccessConfig = accounts.value.reduce((acc, accountBlock) => {
            acc[accountBlock.id] = {
                account: {
                    id: accountBlock.id
                },
                hasAccess: true,
                operationLimits: accountBlock.operationLimits.reduce((operationAcc, operation) => {
                    operationAcc[operation.operationType.value] = {
                        operationType: getOperationsTypesList.value.find(operationType => {
                            return operationType.id === operation.operationType.value
                        }),
                        dailyPlafond: operation.plafond.value,
                        transactionLimit: operation.transactionLimit.value
                    }
                    return operationAcc
                }, {})
            }
            return acc
        }, {})
    }

    try {
        await editOperator({ id: props.operatorId, data: payload })
        showOverlayLoader.value = false
    } catch (error) {
        if (error.toString() === 'Cancel') return
		errorHandler(error.response.data, editOperatorHandler)
    }
}

const headerData = ref(null)
const isEdited = ref(false)
const challengeSuccessHandler = (res) => {
    headerData.value = {
        status: res.errorLevel,
        code: res.code,
        title: res.message,
        additionalMessage: res.data?.additionalMessage ? res.data.additionalMessage : ''
    }

    isEdited.value = true
    scrollValue.value = 0
}

/*************************************************
*                                                *
*             Success/Error Handlers             *
*                                                *
*************************************************/
const showFeedbackToast = ref(false)
const feedbackToastData = ref(emptyObjectConstructor())

const errorHandler = (data, retryFn) => {
    feedbackToastData.value = errorObjectConstructor(data, retryFn, () => { goToStep(1) })
	showFeedbackToast.value = true
    showLoader.value = false
    showOverlayLoader.value = false
}

watch(() => activeStep.value, (newVal, oldVal) => {
    scrollValue.value = 0
})

initialRequests()
</script>

<style lang="scss" scoped>
</style>
